// src/pages/AdminOrders/AdminOrders.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Modal,
  Row,
  Col,
  Form,
} from "react-bootstrap";
import { Auth } from "aws-amplify";
import AdminNav from "../../components/AdminNav";
import "./AdminOrders.css";

const API_BASE = process.env.REACT_APP_API_BASE;

async function authHeader() {
  const session = await Auth.currentSession();
  const token = session.getAccessToken().getJwtToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function statusBadge(status) {
  const s = (status || "").toLowerCase();
  if (s === "paid") return <Badge bg="success">Paid</Badge>;
  if (s === "refunded") return <Badge bg="secondary">Refunded</Badge>;
  if (s === "canceled") return <Badge bg="secondary">Canceled</Badge>;
  return <Badge bg="dark">{status || "unknown"}</Badge>;
}

function inventoryBadge(inv) {
  const s = (inv || "").toLowerCase();
  if (s === "ok") return <Badge bg="success">Inventory OK</Badge>;
  if (s === "failed") return <Badge bg="danger">Inventory Failed</Badge>;
  return <Badge bg="secondary">{inv || "unknown"}</Badge>;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function AdminOrders() {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Filters
  const [statusFilter, setStatusFilter] = useState("all"); // all | paid | refunded | canceled
  const [invFilter, setInvFilter] = useState("all"); // all | ok | failed

  // Details modal
  const [detailModal, setDetailModal] = useState({
    show: false,
    sessionId: "",
    loading: false,
    item: null,
  });

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const idPayload = user?.signInUserSession?.idToken?.payload || {};
        const groups =
          idPayload["cognito:groups"] || idPayload["custom:groups"] || [];

        if (
          Array.isArray(groups) &&
          (groups.includes("Support") || groups.includes("Admin"))
        ) {
          setAuthorized(true);
          await fetchOrders("all", "all");
        } else {
          setAuthorized(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("Auth error:", err);
        setAuthorized(false);
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildOrdersUrl = (status, inv) => {
    let url = `${API_BASE}/admin/orders`;
    const params = [];
    if (status && status !== "all")
      params.push(`status=${encodeURIComponent(status)}`);
    if (inv && inv !== "all")
      params.push(`inventoryStatus=${encodeURIComponent(inv)}`);
    if (params.length) url += `?${params.join("&")}`;
    return url;
  };

  const fetchOrders = async (status = statusFilter, inv = invFilter) => {
    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      if (!API_BASE) {
        throw new Error(
          "Missing REACT_APP_API_BASE. Add it to your env and restart the dev server."
        );
      }

      const headers = await authHeader();
      const url = buildOrdersUrl(status, inv);

      const res = await fetch(url, { method: "GET", headers });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.error || `Failed to load orders: ${res.status}`);
      }

      const data = await res.json();
      setOrders(data.items || []);
    } catch (err) {
      console.error("Fetch orders error:", err);
      setError(err?.message || "Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  const onChangeFilters = async (nextStatus, nextInv) => {
    setStatusFilter(nextStatus);
    setInvFilter(nextInv);
    await fetchOrders(nextStatus, nextInv);
  };

  const openDetails = async (sessionId) => {
    if (!sessionId) return;
    setError("");
    setSuccessMsg("");

    setDetailModal({
      show: true,
      sessionId,
      loading: true,
      item: null,
    });

    try {
      const headers = await authHeader();
      const res = await fetch(
        `${API_BASE}/admin/orders/${encodeURIComponent(sessionId)}`,
        { method: "GET", headers }
      );

      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.error || `Failed to load order details: ${res.status}`);
      }

      const data = await res.json();
      setDetailModal((prev) => ({
        ...prev,
        loading: false,
        item: data.item || null,
      }));
    } catch (err) {
      console.error("Order details error:", err);
      setError(err?.message || "Failed to load order details.");
      setDetailModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const reconcileOrder = async (sessionId) => {
    if (!sessionId) return;

    setError("");
    setSuccessMsg("");

    try {
      const headers = await authHeader();
      const res = await fetch(
        `${API_BASE}/admin/orders/${encodeURIComponent(sessionId)}/reconcile`,
        { method: "POST", headers, body: JSON.stringify({}) }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body.message || body.error || `Reconcile failed: ${res.status}`
        );
      }

      setSuccessMsg(`Inventory reconciled for ${sessionId}.`);
      await fetchOrders(statusFilter, invFilter);
    } catch (err) {
      console.error("Reconcile error:", err);
      setError(err?.message || "Failed to reconcile.");
    }
  };

  const closeDetails = () => {
    setDetailModal({ show: false, sessionId: "", loading: false, item: null });
  };

  const rows = useMemo(() => {
    return [...orders].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }, [orders]);

  if (loading && !orders.length) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4 text-center">
            <Spinner animation="border" /> Loading orders…
          </div>
        </Container>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4">
            <Alert variant="danger">You’re not authorized to view this page.</Alert>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <Container className="admin-page-shell">
        <AdminNav />

        <div className="admin-page-header">
          <div className="admin-page-header-left">
            <div className="admin-page-header-icon">🧾</div>
            <div>
              <h3 className="admin-page-title">Orders</h3>
              <div className="admin-page-subtitle">
                Review paid orders and inventory status. Newest first.
              </div>
            </div>
          </div>

          <div className="admin-page-header-right">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => fetchOrders(statusFilter, invFilter)}
            >
              Refresh
            </Button>
          </div>
        </div>

        <div className="admin-page-body">
          {error && <Alert variant="danger">{error}</Alert>}
          {successMsg && <Alert variant="success">{successMsg}</Alert>}

          <div className="admin-panel mb-3">
            <div className="admin-orders-filters">
              <Form.Group>
                <Form.Label className="mb-1">Status</Form.Label>
                <Form.Select
                  size="sm"
                  value={statusFilter}
                  onChange={(e) => onChangeFilters(e.target.value, invFilter)}
                >
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="refunded">Refunded</option>
                  <option value="canceled">Canceled</option>
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label className="mb-1">Inventory</Form.Label>
                <Form.Select
                  size="sm"
                  value={invFilter}
                  onChange={(e) => onChangeFilters(statusFilter, e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="ok">OK</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </Form.Group>

              <Button
                className="admin-orders-refresh"
                variant="outline-secondary"
                size="sm"
                onClick={() => fetchOrders(statusFilter, invFilter)}
              >
                Refresh
              </Button>
            </div>
          </div>

          <div className="admin-panel">
            {rows.length === 0 ? (
              <Alert variant="info" className="mb-0">
                No orders yet. Once a Stripe checkout succeeds, the webhook will
                write a row to <strong>lpp_orders-dev</strong>.
              </Alert>
            ) : (
              <Table className="admin-table" striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Status</th>
                    <th>Inventory</th>
                    <th>SKU</th>
                    <th>Qty</th>
                    <th>Session</th>
                    <th>PaymentIntent</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((o) => (
                    <tr key={o.sessionId}>
                      <td style={{ whiteSpace: "nowrap" }}>{o.createdAt || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{statusBadge(o.status)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {inventoryBadge(o.inventoryStatus)}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>{o.sku || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{o.qty ?? "—"}</td>

                      <td style={{ maxWidth: 220, wordBreak: "break-all" }}>
                        {o.sessionId || "—"}
                      </td>
                      <td style={{ maxWidth: 220, wordBreak: "break-all" }}>
                        {o.paymentIntentId || "—"}
                      </td>

                      <td>
                        <div className="d-flex flex-column gap-1 admin-row-actions">
                          {String(o.inventoryStatus || "").toLowerCase() ===
                            "failed" && (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => reconcileOrder(o.sessionId)}
                            >
                              Reconcile Inventory
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => openDetails(o.sessionId)}
                          >
                            View Details
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={async () => {
                              const ok = await copyToClipboard(o.sessionId || "");
                              setSuccessMsg(ok ? "Session ID copied." : "Copy failed.");
                            }}
                            disabled={!o.sessionId}
                          >
                            Copy Session
                          </Button>

                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={async () => {
                              const ok = await copyToClipboard(o.paymentIntentId || "");
                              setSuccessMsg(ok ? "PaymentIntent copied." : "Copy failed.");
                            }}
                            disabled={!o.paymentIntentId}
                          >
                            Copy PaymentIntent
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* Details Modal */}
          <Modal className="admin-modal" show={detailModal.show} onHide={closeDetails} centered>
            <Modal.Header closeButton>
              <Modal.Title>Order Details</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {detailModal.loading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" /> Loading…
                </div>
              ) : !detailModal.item ? (
                <Alert variant="warning" className="mb-0">
                  No details found for this order.
                </Alert>
              ) : (
                <Row className="g-2">
                  <Col xs={12}>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Session ID
                    </div>
                    <div style={{ wordBreak: "break-all" }}>
                      <strong>{detailModal.item.sessionId}</strong>
                    </div>
                  </Col>

                  <Col xs={12}>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      PaymentIntent
                    </div>
                    <div style={{ wordBreak: "break-all" }}>
                      {detailModal.item.paymentIntentId || "—"}
                    </div>
                  </Col>

                  <Col xs={6}>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      SKU
                    </div>
                    <div>{detailModal.item.sku || "—"}</div>
                  </Col>

                  <Col xs={6}>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Qty
                    </div>
                    <div>{detailModal.item.qty ?? "—"}</div>
                  </Col>

                  <Col xs={6}>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Status
                    </div>
                    <div>{statusBadge(detailModal.item.status)}</div>
                  </Col>

                  <Col xs={6}>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Inventory
                    </div>
                    <div>{inventoryBadge(detailModal.item.inventoryStatus)}</div>
                  </Col>

                  <Col xs={12}>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Created
                    </div>
                    <div>{detailModal.item.createdAt || "—"}</div>
                  </Col>

                  <Col xs={12}>
                    <div className="text-muted mt-2" style={{ fontSize: "0.9rem" }}>
                      Raw
                    </div>
                    <pre className="admin-orders-raw">
                      {JSON.stringify(detailModal.item, null, 2)}
                    </pre>
                  </Col>
                </Row>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeDetails}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </Container>
    </div>
  );
}
